import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';
const FORM_URL = 'https://readdy.ai/api/form/d74j8rn5hic0eqh316j0';

export default function HamiltonCountyLifeInsurance() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    coverage_amount: '', city: '', age: '', sms_consent: false,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance Hamilton County Indiana | Fishers, Noblesville, Carmel & Westfield | Hoosier Life';

    const setMeta = (name: string, content: string, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.content = content;
    };

    setMeta('description', 'Life insurance for Hamilton County, Indiana — Fishers, Noblesville, Carmel, Westfield & all HC cities. Independent agency comparing 20+ carriers. Highest-income county in Indiana. Free quotes at (317) 648-2983.');
    setMeta('keywords', 'life insurance Hamilton County Indiana, Hamilton County IN insurance agent, life insurance Fishers Noblesville Carmel Westfield, Hamilton County Indiana life insurance rates');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Hamilton County, Indiana');
    setMeta('geo.position', '40.0559;-86.0581');
    setMeta('ICBM', '40.0559, -86.0581');
    setMeta('og:title', 'Life Insurance Hamilton County Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'Serving Fishers, Noblesville, Carmel, Westfield and all of Hamilton County. Indiana\'s fastest-growing county. Independent agency, 20+ carriers. (317) 648-2983.', true);
    setMeta('og:url', `${SITE_URL}/life-insurance-hamilton-county`, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/life-insurance-hamilton-county`;

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/life-insurance-hamilton-county#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How much is life insurance in Hamilton County, Indiana?',
          acceptedAnswer: { '@type': 'Answer', text: 'Hamilton County residents pay the same Indiana statewide rates as Indianapolis residents. A healthy 35-year-old in Fishers, Noblesville, or Carmel pays approximately $28–$32/month for $500,000 in 20-year term life coverage. Indiana rates run 8–12% below the national average.' },
        },
        {
          '@type': 'Question',
          name: 'What cities in Hamilton County does Hoosier Life Insurance serve?',
          acceptedAnswer: { '@type': 'Answer', text: 'Hoosier Life Insurance serves all Hamilton County cities including Fishers, Noblesville, Carmel, Westfield, Cicero, Sheridan, Arcadia, Atlanta, and all unincorporated areas. All consultations are by phone or Zoom — no office visit needed.' },
        },
        {
          '@type': 'Question',
          name: 'Why do Hamilton County families need more life insurance than the Indiana average?',
          acceptedAnswer: { '@type': 'Answer', text: 'Hamilton County has Indiana\'s highest median household income at $85,000+ and average home values above $375,000. Most financial advisors recommend 10–12× income in coverage. A $90,000 HC earner needs $900,000–$1,080,000 minimum — far more than the state\'s typical $180,000 average coverage gap.' },
        },
        {
          '@type': 'Question',
          name: 'Is it better to get life insurance from a local Hamilton County agent or online?',
          acceptedAnswer: { '@type': 'Answer', text: 'A local independent agent who knows Hamilton County — its employers, home values, and schools — will find you better coverage at better rates than a generic online platform. Hoosier Life Insurance is local to Central Indiana, shops 20+ carriers, and provides personalized advice for Fishers, Noblesville, Carmel and Westfield families.' },
        },
      ],
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/hamilton-county-life-insurance#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/service-areas` },
        { '@type': 'ListItem', position: 3, name: 'Life Insurance Hamilton County Indiana', item: `${SITE_URL}/hamilton-county-life-insurance` },
      ],
    };

    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${SITE_URL}/life-insurance-hamilton-county#speakable`,
      speakable: { '@type': 'SpeakableSpecification', xpath: ['/html/body//*[@id="hc-price-summary"]'] },
      url: `${SITE_URL}/life-insurance-hamilton-county`,
    };

    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': ['InsuranceAgency', 'LocalBusiness'],
      '@id': `${SITE_URL}/hamilton-county-life-insurance#localbusiness`,
      name: 'Hoosier Life Insurance — Hamilton County Indiana',
      url: `${SITE_URL}/hamilton-county-life-insurance`,
      telephone: '+13176482983',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Noblesville',
        addressRegion: 'IN',
        postalCode: '46060',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 40.0559,
        longitude: -86.0581,
      },
      areaServed: {
        '@type': 'County',
        name: 'Hamilton County',
        containedInPlace: { '@type': 'State', name: 'Indiana' },
      },
      description: 'Independent life insurance agency serving all of Hamilton County, Indiana — Fishers, Noblesville, Carmel, Westfield, Cicero, Sheridan, and surrounding areas.',
      aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '127', bestRating: '5', worstRating: '1' },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'], opens: '09:00', closes: '18:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '10:00', closes: '14:00' },
      ],
      priceRange: '$$',
      sameAs: [
        'https://www.facebook.com/HoosierLifeInsurance',
        'https://twitter.com/HoosierLifeIns',
        'https://www.linkedin.com/company/hoosier-life-insurance',
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Life Insurance Products',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', description: '10, 20, and 30-year term policies from 20+ A-rated carriers' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', description: 'Permanent whole life coverage with guaranteed cash value growth' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Mortgage Protection Insurance', description: 'Decreasing term policy tied to mortgage balance for homeowners' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', description: 'Simplified issue whole life covering burial and end-of-life costs' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'No Medical Exam Life Insurance', description: 'Same-day approval policies with no blood draw or doctor visit required' } },
        ],
      },
    };

    const schemasToInject = [faqSchema, breadcrumbSchema, speakableSchema, localBusinessSchema];
    schemasToInject.forEach((schema, idx) => {
      const id = `schema-hc-${idx}`;
      if (document.getElementById(id)) return;
      const el = document.createElement('script');
      el.id = id; el.type = 'application/ld+json'; el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
    });

    return () => { schemasToInject.forEach((_, idx) => document.getElementById(`schema-hc-${idx}`)?.remove()); };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, String(v)));
      await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setFormSubmitted(true);
    } catch { setFormSubmitted(true); }
  };

  const cities = [
    { name: 'Fishers', url: '/life-insurance-fishers', img: 'https://public.readdy.ai/ai/img_res/76e3e53a00aad7cacc78abb2e76db02b.jpg', income: '$95K median income', pop: '104K residents', employers: 'Roche, Navient, HSE Schools', badge: 'Most Searched' },
    { name: 'Noblesville', url: '/life-insurance-noblesville', img: 'https://readdy.ai/api/search-image?query=Noblesville%20Indiana%20historic%20downtown%20square%20community%20families%20Morse%20Reservoir%20aerial%20view%20suburban%20neighborhoods&width=600&height=400&seq=hc-noblesville&orientation=landscape', income: '$88K median income', pop: '72K residents', employers: 'Riverview Health, Hamilton County Gov', badge: '#2 Fastest Growing' },
    { name: 'Carmel', url: '/life-insurance-carmel', img: 'https://readdy.ai/api/search-image?query=Carmel%20Indiana%20luxury%20suburban%20community%20Village%20of%20West%20Clay%20upscale%20neighborhoods%20aerial%20view%20high%20income%20families%20beautiful%20homes&width=600&height=400&seq=hc-carmel&orientation=landscape', income: '$109K median income', pop: '107K residents', employers: 'CNO Financial, Duke Energy', badge: 'Highest Income in IN' },
    { name: 'Westfield', url: '/service-areas/westfield', img: 'https://readdy.ai/api/search-image?query=Westfield%20Indiana%20fast%20growing%20city%20Grand%20Park%20sports%20complex%20new%20construction%20suburban%20development%20warm%20golden%20hour%20aerial&width=600&height=400&seq=hc-westfield&orientation=landscape', income: '$85K median income', pop: '53K residents', employers: 'Grand Park, Freedom Real Estate', badge: 'Fastest Growing City' },
  ];

  const faqs = [
    { q: 'How much is life insurance in Hamilton County, Indiana?', a: 'Hamilton County residents pay Indiana statewide rates based on age, health, and coverage — not city. A healthy 35-year-old in Fishers, Noblesville, Carmel, or Westfield pays approximately $28–$32/month for $500,000 in 20-year term life coverage. Indiana rates run 8–12% below the national average.' },
    { q: 'Why do HC families need more coverage than the Indiana average?', a: 'Hamilton County has Indiana\'s highest median income ($85K+) and average home values above $375,000. Most financial advisors recommend 10–12× income. An $85K HC earner needs $850K–$1M minimum — yet the average Indiana resident carries only $180,000. That $670K+ gap is what we help close.' },
    { q: 'Does Hoosier Life Insurance serve all Hamilton County cities?', a: 'Yes — all HC cities including Fishers, Noblesville, Carmel, Westfield, Cicero, Sheridan, Arcadia, Atlanta, and all unincorporated areas. All consultations are by phone or Zoom. No office visit needed anywhere in the county.' },
    { q: 'Is a local Hamilton County agent better than an online insurer?', a: 'A local independent agent who understands HC\'s home values, school district costs, and major employers finds better-fit coverage than a national platform. We\'re Central Indiana based, shop 20+ carriers, and know the difference between a Carmel Village of West Clay mortgage and a Westfield starter home coverage need.' },
  ];

  return (
    <>
      <title>Life Insurance Hamilton County Indiana | Fishers, Noblesville, Carmel & Westfield | Hoosier Life</title>
      <meta name="description" content="Life insurance for Hamilton County, Indiana families — serving Carmel, Fishers, Noblesville & Westfield. Independent agency comparing 20+ carriers. Free quotes. Call (317) 648-2983." />
      <link rel="canonical" href="https://hoosierlifeinsurance.com/hamilton-county-life-insurance" />
      <meta name="robots" content="index, follow" />
      <meta property="og:title" content="Life Insurance Hamilton County Indiana | Fishers, Noblesville, Carmel & Westfield | Hoosier Life" />
      <meta property="og:url" content="https://hoosierlifeinsurance.com/hamilton-county-life-insurance" />
      <meta name="geo.placename" content="Hamilton County, Indiana" />
      <meta name="geo.region" content="US-IN" />
      <meta name="geo.position" content="40.0456;-86.0086" />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1A3A52] via-[#162E43] to-[#0F2A3F] text-white pt-24 pb-14">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex items-center gap-2 text-sm text-white/50">
              <li><Link to="/" className="hover:text-[#D97642] transition-colors">Home</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-[#D97642] font-medium">Life Insurance Hamilton County IN</li>
            </ol>
          </nav>
          <div className="text-center max-w-[760px] mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] text-xs font-bold px-3 py-1.5 rounded-full mb-5">
              <i className="ri-map-2-line"></i>
              Fishers · Noblesville · Carmel · Westfield · All Hamilton County
            </div>
            <h1 className="text-[40px] md:text-[52px] font-bold leading-tight mb-4">
              Life Insurance for<br />Hamilton County Families — Independent &amp; Local
            </h1>
            <p className="text-[18px] text-white/70 leading-relaxed mb-8 max-w-[600px] mx-auto">
              Indiana's fastest-growing county. Highest household income in the state. Your coverage needs more than a group plan — we compare 20+ carriers to find exactly what HC families need.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="#hc-quote-form" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-search-line"></i>Get My Free HC Rate
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line"></i>(317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* County stats bar */}
      <section className="bg-[#D97642] text-white py-5">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { val: '370K+', label: 'County Residents' },
            { val: '$85K+', label: 'Median Household Income' },
            { val: '$375K+', label: 'Avg Home Value' },
            { val: 'Top 5', label: 'Fastest-Growing US County' },
          ].map((s, i) => (
            <div key={i}><p className="text-[24px] font-black">{s.val}</p><p className="text-[13px] text-white/80 font-medium">{s.label}</p></div>
          ))}
        </div>
      </section>

      {/* City grid */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-[30px] md:text-[36px] font-bold text-[#1A3A52] mb-3">Choose Your Hamilton County City</h2>
            <p className="text-[15px] text-[#718096] max-w-[560px] mx-auto">Each city has its own dedicated page with neighborhood-specific content, employer callouts, and local rate data.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {cities.map((city, i) => (
              <Link key={i} to={city.url} className="group block bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#D97642] transition-all cursor-pointer">
                <div className="relative h-44 overflow-hidden">
                  <img src={city.img} alt={`Life insurance ${city.name} Indiana`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" width={600} height={300} loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A3A52]/70 to-transparent"></div>
                  <span className="absolute top-3 right-3 bg-[#D97642] text-white text-[11px] font-bold px-2.5 py-1 rounded-full">{city.badge}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[20px] font-bold text-[#1A3A52] group-hover:text-[#D97642] transition-colors">{city.name}, IN</h3>
                    <span className="text-[13px] font-semibold text-[#D97642]">{city.income}</span>
                  </div>
                  <p className="text-[13px] text-[#718096] mb-3">{city.pop} · Key employers: {city.employers}</p>
                  <span className="inline-flex items-center gap-1.5 text-[#D97642] text-[13px] font-semibold group-hover:gap-3 transition-all">
                    View {city.name} life insurance page <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why HC families need more coverage */}
      <section className="py-14 bg-[#F7F9FB]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-[28px] md:text-[34px] font-bold text-[#1A3A52] mb-4">Why Hamilton County Families Are Among Indiana's Most Underinsured</h2>
              <p className="text-[15px] text-[#718096] leading-relaxed mb-5">
                With the state's highest incomes and most expensive homes, Hamilton County families have the most to lose — and the most to cover. Yet the average HC resident carries the same $180,000 in coverage as a statewide average that assumes much lower incomes and mortgages.
              </p>
              <div className="space-y-3">
                {[
                  { label: 'HC Avg Mortgage Balance', value: '$320,000+', note: 'Above Indiana avg of $187K' },
                  { label: 'HC Avg Household Income', value: '$85,000+', note: 'Requires $850K–$1M+ coverage' },
                  { label: 'HC Avg Life Insurance Carried', value: '~$180,000', note: 'Same as statewide — way too low' },
                  { label: 'Coverage Gap', value: '$670,000+', note: 'Per family, on average' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between bg-white rounded-xl p-4 border border-gray-100">
                    <span className="text-[14px] text-[#4A5568]">{row.label}</span>
                    <div className="text-right">
                      <span className={`font-bold text-[15px] ${i === 3 ? 'text-red-500' : 'text-[#1A3A52]'}`}>{row.value}</span>
                      <p className="text-[11px] text-[#718096]">{row.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div id="hc-price-summary">
              <h3 className="text-[22px] font-bold text-[#1A3A52] mb-4">Hamilton County Life Insurance Rates — 2025</h3>
              <p className="text-[14px] text-[#718096] mb-4">Applies across all HC cities (Fishers, Noblesville, Carmel, Westfield). Healthy, non-smoking residents.</p>
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1A3A52] text-white">
                      {['Age', 'Coverage', 'Male/Mo', 'Female/Mo'].map(h => (
                        <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      { age: '30', cov: '$500K / 20yr', m: '$28', f: '$23' },
                      { age: '35', cov: '$500K / 20yr', m: '$32', f: '$27' },
                      { age: '35', cov: '$1M / 20yr', m: '$51', f: '$43' },
                      { age: '40', cov: '$500K / 20yr', m: '$45', f: '$38' },
                      { age: '45', cov: '$500K / 20yr', m: '$68', f: '$56' },
                      { age: '50', cov: '$250K / 20yr', m: '$72', f: '$60' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                        <td className="px-4 py-3 font-semibold text-[#1A3A52]">{row.age}</td>
                        <td className="px-4 py-3 text-[#4A5568]">{row.cov}</td>
                        <td className="px-4 py-3 font-bold text-[#D97642]">{row.m}/mo</td>
                        <td className="px-4 py-3 font-bold text-[#D97642]">{row.f}/mo</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-[#718096] mt-2 italic">*Preferred Plus, non-smoker. Actual rates vary.</p>
              <a href="#hc-quote-form" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#D97642] text-white text-[13px] font-bold rounded-lg hover:bg-[#C26638] transition-colors cursor-pointer whitespace-nowrap">
                Get Your Exact Hamilton County Rate →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="hc-quote-form" className="py-16 bg-[#FDF6F0]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-[30px] font-bold text-[#1A3A52] mb-3">Get Your Free Hamilton County Quote</h2>
              <p className="text-[15px] text-[#718096] mb-6">Tell us your city and we'll send back a rate comparison from 20+ carriers in under 60 minutes.</p>
              <div className="space-y-4">
                {[
                  { icon: 'ri-map-pin-2-line', title: 'City-Specific Advice', body: 'Different cities have different typical mortgages, school costs, and employer benefit gaps. We tailor coverage recommendations to your specific HC city.' },
                  { icon: 'ri-scales-3-line', title: '20+ Carriers Compared', body: 'Mutual of Omaha, Prudential, Nationwide, Banner Life, AIG, and 15+ more — shopped simultaneously so you don\'t have to.' },
                  { icon: 'ri-phone-line', title: 'Phone or Zoom — Your Choice', body: 'No office visit. No drive to Carmel or Noblesville. We work with families across all of Hamilton County by phone or video.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                    </div>
                    <div>
                      <p className="font-bold text-[#1A3A52] text-[14px] mb-0.5">{item.title}</p>
                      <p className="text-[13px] text-[#718096] leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              {formSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-green-600 text-3xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#1A3A52] mb-2">Got it — thanks!</h3>
                  <p className="text-[#718096] text-sm">Benjamin will reach out within 1 business hour with your Hamilton County rate comparison.</p>
                </div>
              ) : (
                <form id="hc-quote-form-el" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <input type="hidden" name="source_page" value="Hamilton County Life Insurance Hub Page" />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">First Name *</label>
                      <input name="first_name" type="text" required value={formData.first_name} onChange={handleInputChange} placeholder="Alex" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Last Name *</label>
                      <input name="last_name" type="text" required value={formData.last_name} onChange={handleInputChange} placeholder="Miller" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Email *</label>
                    <input name="email" type="email" required value={formData.email} onChange={handleInputChange} placeholder="alex@example.com" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Phone *</label>
                    <input name="phone" type="tel" required value={formData.phone} onChange={handleInputChange} placeholder="(317) 555-0100" className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642]" />
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Your City</label>
                    <select name="city" value={formData.city} onChange={handleInputChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer">
                      <option value="">Select your city</option>
                      {['Fishers','Noblesville','Carmel','Westfield','Cicero','Sheridan','Other Hamilton County'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Your Age *</label>
                      <select name="age" required value={formData.age} onChange={handleInputChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer">
                        <option value="">Select</option>
                        {['18–25','26–30','31–35','36–40','41–45','46–50','51–55','56–60','61–65','65+'].map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">Coverage Amount</label>
                      <select name="coverage_amount" value={formData.coverage_amount} onChange={handleInputChange} className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#D97642] cursor-pointer">
                        <option value="">Not sure</option>
                        {['$250K–$500K','$500K–$750K','$750K–$1M','$1M–$2M','$2M+'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-[#F7F9FB] rounded-lg p-3.5">
                    <input type="checkbox" name="sms_consent" id="sms-hc" checked={formData.sms_consent} onChange={handleInputChange} className="mt-0.5 cursor-pointer" />
                    <label htmlFor="sms-hc" className="text-[12px] text-[#718096] leading-relaxed cursor-pointer">
                      I agree to receive SMS/text messages from Hoosier Life Insurance. Message &amp; data rates may apply. Reply STOP to opt out.
                    </label>
                  </div>
                  <button type="submit" className="w-full py-3.5 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-colors text-[16px] cursor-pointer whitespace-nowrap">
                    Get My Hamilton County Rate →
                  </button>
                  <p className="text-[11px] text-[#718096] text-center">No spam. No pressure. Cancel anytime.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="max-w-[800px] mx-auto px-4 sm:px-8">
          <h2 className="text-[28px] font-bold text-[#1A3A52] mb-2 text-center">Hamilton County Life Insurance — FAQs</h2>
          <p className="text-[14px] text-[#718096] mb-8 text-center">Common questions from HC families across Fishers, Noblesville, Carmel, and Westfield.</p>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F7F9FB] transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-[#1A3A52] text-[15px] pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#D97642] text-xl flex-shrink-0`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-[#4A5568] text-[14px] leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Hub Callout */}
      <section className="py-9 bg-[#0F2A3F]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 border border-white/10 rounded-2xl px-7 py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#D97642] rounded-xl flex-shrink-0">
                <i className="ri-book-open-line text-white text-[22px]"></i>
              </div>
              <div>
                <p className="text-[11px] text-[#D97642] font-bold uppercase tracking-widest mb-1">Indiana Life Insurance Learning Hub</p>
                <h3 className="text-[19px] font-bold text-white leading-tight mb-1">Not sure where to start with coverage?</h3>
                <p className="text-[14px] text-white/65">27 Indiana guides + 5 pillar posts + 7 city breakdowns — all in one place.</p>
              </div>
            </div>
            <Link to="/learning-hub" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap flex-shrink-0">
              <i className="ri-compass-3-line text-[17px]"></i>
              Explore the Learning Hub
              <i className="ri-arrow-right-line text-[15px]"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-14 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white text-center">
        <div className="max-w-[600px] mx-auto px-4 sm:px-8">
          <i className="ri-map-2-line text-[#D97642] text-4xl mb-4 block"></i>
          <h2 className="text-[28px] font-bold mb-3">Wherever You Live in Hamilton County — We've Got You</h2>
          <p className="text-white/70 text-[15px] mb-7 max-w-[480px] mx-auto">Phone and Zoom consultations. Same 20+ carriers. Local knowledge that national platforms can't match.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#hc-quote-form" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-[#D97642] text-white font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line"></i>Get My Free Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-line"></i>(317) 648-2983
            </a>
          </div>
        </div>
      </section>

      {/* Statewide Indiana cross-links */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8">
          <p className="text-center text-[13px] font-bold text-[#718096] uppercase tracking-widest mb-4">Also Serving Indiana Statewide</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Life Insurance Indianapolis', href: '/life-insurance-indianapolis', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Fort Wayne', href: '/life-insurance-fort-wayne', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance South Bend', href: '/life-insurance-south-bend', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Evansville', href: '/life-insurance-evansville', icon: 'ri-map-pin-line' },
              { label: 'Life Insurance Indiana (Statewide)', href: '/life-insurance-indiana', icon: 'ri-map-2-line' },
              { label: 'No Medical Exam Options', href: '/no-medical-exam', icon: 'ri-stethoscope-line' },
              { label: 'All Service Areas', href: '/service-areas', icon: 'ri-compass-3-line' },
            ].map(link => (
              <Link key={link.label} to={link.href} className="flex items-center gap-2 px-4 py-2 bg-[#F7F9FB] border border-gray-200 rounded-lg text-[13px] font-medium text-[#1A3A52] hover:border-[#D97642] hover:text-[#D97642] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className={`${link.icon} text-[#D97642] text-[14px]`}></i></div>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
